import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { isStaleBuildError } from './staleBuild'

export { isStaleBuildError }

interface Props {
  children: ReactNode
  label?: string
}

interface State {
  error: Error | null
}

/**
 * Minimal error boundary: if a component throws, the user sees a clear
 * message instead of a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[WonderWorks] UI error:', error, info.componentStack)
  }

  render() {
    if (this.state.error && isStaleBuildError(this.state.error)) {
      return (
        <div style={{ padding: 24 }}>
          <div className="ww-error">
            <strong>WonderWorks has been updated.</strong>
            <div style={{ marginTop: 6 }}>Reload the page to carry on with the new version.</div>
            <button
              type="button"
              className="ww-btn ww-btn--primary ww-btn--sm"
              style={{ marginTop: 12 }}
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          </div>
        </div>
      )
    }
    if (this.state.error) {
      return (
        <div style={{ padding: 24 }}>
          <div className="ww-error">
            <strong>Something went wrong in {this.props.label ?? 'WonderWorks'}.</strong>
            <div style={{ marginTop: 6, fontFamily: 'var(--ww-font-mono)', fontSize: 12 }}>
              {this.state.error.message}
            </div>
            <button
              type="button"
              className="ww-btn ww-btn--secondary ww-btn--sm"
              style={{ marginTop: 12 }}
              onClick={() => this.setState({ error: null })}
            >
              Try again
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
